// Pure helpers for Stock Control › Write Off. Kept out of the page so the
// selection maths (what may be written off, when to warn, when to block)
// is unit-testable without rendering anything.

// "pallet_sold" (BE migration 209): a sale pallet shipped and took its
// contents with it. Portal writes these automatically when a pallet order
// is fulfilled; it is offered here for a pallet sold outside Portal.
// "unsold_grey" (BE migration 247): an Unsold Grey item confirmed into its
// Sorter 1 slot. Written automatically by the loader's confirm; offered
// here for a grey unit handled by hand. "duplicate": a unit booked in twice.
export const WRITE_OFF_REASONS = ["damaged", "disposed", "sold_offline", "pallet_sold", "unsold_grey", "duplicate", "stock_take", "other"] as const;
export type WriteOffReason = (typeof WRITE_OFF_REASONS)[number];

export const isWriteOffReason = (v: unknown): v is WriteOffReason =>
    typeof v === "string" && (WRITE_OFF_REASONS as readonly string[]).includes(v);

// i18n key for a reason code (write_off_reason_damaged, …).
export const reasonKey = (reason: string) => `write_off_reason_${reason}`;

// One row of the write-off table. By location, a row is a SKU at the
// searched location; by SKU, a row is a location holding the searched SKU
// (`location` is set and the SKU fields repeat the product). lineKey()
// tells the two apart for selection state.
export interface WriteOffLine {
    sku: string;
    title: string;
    barcode?: string | null;
    thumbnail_url?: string | null;
    image_url?: string | null;
    /** By SKU only: the location barcode this row is, and how to show it. */
    location?: string;
    location_name?: string;
    sellable?: "sellable" | "unsellable";
    /** Units the books say are on this shelf (stock-check expected basis). */
    units_here: number;
    /** Of those, units an open allocation points at — never writable. */
    allocated_qty: number;
    /** units_here − allocated_qty, as the BE computed it. */
    writable_qty: number;
    /** SKU-wide free stock at the warehouse — the "sold offline" ceiling. */
    free_stock: number;
    unit_cost: number;
    line_cost: number;
    /**
     * When the units were put at this location (Alex, 2026-11-01): the
     * oldest and newest live stock row (ISO) and a per-day breakdown of the
     * units still here, in warehouse-local days. Absent/empty when the BE
     * found no live row to date (never a made-up date).
     */
    received_first?: string | null;
    received_last?: string | null;
    received_dates?: ReceivedDay[];
}

/** Units of a row put at the location on one warehouse-local day (YYYY-MM-DD). */
export interface ReceivedDay {
    date: string;
    qty: number;
}

// Selection key: the location barcode in by-SKU mode, else the SKU.
export const lineKey = (line: WriteOffLine): string => line.location ?? line.sku;

export interface WriteOffLocation {
    barcode: string;
    warehouse: string;
    display_name: string;
    sellable: "sellable" | "unsellable";
}

// The product header of the by-SKU screen (GET /write-off/sku).
export interface WriteOffProduct {
    sku: string;
    title: string;
    barcode: string | null;
    thumbnail_url: string | null;
    image_url: string | null;
    /** SKU-wide free stock at the warehouse — shared by every location row. */
    free_stock: number;
    /** Units across every location listed. */
    total_units: number;
}

export type SearchMode = "location" | "sku";

// ---- Row filter (Alex, 2026-10-26) ----------------------------------
//
// Four chips above the table narrow the rows by what the figures say
// about them. A row can carry several flags (3 of 10 allocated is both
// "on hand" and "allocated"), so the chips are OR-ed: a row shows when it
// has ANY ticked flag, and no ticked chip means no filter. Flags are
// derived on the FE from figures the BE already sends — nothing new on
// the wire.
export const STOCK_FLAGS = ["on_hand", "allocated", "reserved", "unsellable"] as const;
export type StockFlag = (typeof STOCK_FLAGS)[number];

// i18n key for a flag (write_off_filter_on_hand, …).
export const flagKey = (flag: StockFlag) => `write_off_filter_${flag}`;

export interface FlagContext {
    /** By location the whole shelf is sellable or not; rows carry no `sellable`. */
    locationSellable?: "sellable" | "unsellable";
}

// What a row is:
//   on_hand     it has unallocated units — something could be written off
//   allocated   an open allocation points at units here
//   reserved    SKU-wide reservations bite into this row: more unallocated
//               units here than the SKU has free, so a write-off beyond
//               free stock would come out of promised units
//   unsellable  the units sit at an unsellable location
export const lineFlags = (line: WriteOffLine, ctx: FlagContext = {}): StockFlag[] => {
    const out: StockFlag[] = [];
    if (line.writable_qty > 0) out.push("on_hand");
    if (line.allocated_qty > 0) out.push("allocated");
    if (line.writable_qty > line.free_stock) out.push("reserved");
    if ((line.sellable ?? ctx.locationSellable) === "unsellable") out.push("unsellable");
    return out;
};

// Rows with any of the active flags; every row when none is active.
export const filterLines = (lines: WriteOffLine[], active: readonly StockFlag[], ctx: FlagContext = {}): WriteOffLine[] => {
    if (active.length === 0) return lines;
    const wanted = new Set(active);
    return lines.filter((l) => lineFlags(l, ctx).some((f) => wanted.has(f)));
};

// How many rows carry each flag — shown on the chips.
export const flagCounts = (lines: WriteOffLine[], ctx: FlagContext = {}): Record<StockFlag, number> => {
    const out: Record<StockFlag, number> = { on_hand: 0, allocated: 0, reserved: 0, unsellable: 0 };
    for (const l of lines) for (const f of lineFlags(l, ctx)) out[f] += 1;
    return out;
};

// ---- Put-here date filter (Alex, 2026-11-01) --------------------------
//
// Each row says when its units were put at the location (the BE reads
// inventory_detail.received_at, which every put-away / Move Stock stamps),
// and a from/to pair above the table narrows the rows to those with at
// least one unit put there inside the range. Days are YYYY-MM-DD strings in
// the warehouse's own time zone, straight from the BE and the date inputs,
// so they compare as strings. Like the flag chips, this only changes what
// is SHOWN: a ticked row that drops out of the range stays ticked. NOTE a
// write-off still removes the OLDEST units of the row first, whatever the
// filter says — the "in range" count is there so that is visible.
export interface DateRange {
    from: string;
    to: string;
}
export const EMPTY_DATE_RANGE: DateRange = { from: "", to: "" };
export const dateRangeActive = (range: DateRange): boolean => Boolean(range.from || range.to);

const normalizeReceived = (raw: unknown): ReceivedDay[] =>
    (Array.isArray(raw) ? raw : [])
        .map((d: any) => ({ date: String(d?.date ?? ""), qty: Number(d?.qty ?? 0) }))
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d.date) && Number.isFinite(d.qty) && d.qty > 0)
        .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

const dayInRange = (day: string, range: DateRange): boolean =>
    (!range.from || day >= range.from) && (!range.to || day <= range.to);

// Units of the row put at the location inside the range (both bounds
// inclusive, either may be open). The whole row when the range is empty.
export const unitsPutHereBetween = (line: WriteOffLine, range: DateRange): number =>
    (line.received_dates ?? []).reduce((a, d) => a + (dayInRange(d.date, range) ? d.qty : 0), 0);

// Rows with at least one unit put here inside the range; every row when
// the range is empty. A row with no dates at all never matches an active
// range — unknown is not "any time".
export const filterByPutHere = (lines: WriteOffLine[], range: DateRange): WriteOffLine[] => {
    if (!dateRangeActive(range)) return lines;
    return lines.filter((l) => unitsPutHereBetween(l, range) > 0);
};

// dd/mm/yyyy from a YYYY-MM-DD day; anything else is shown as sent.
export const formatDay = (day: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : day;
};

// What the "Put here" cell says: the one day, or "oldest – newest" when
// the units arrived on different days. Null when nothing is known.
export const putHereLabel = (line: WriteOffLine): string | null => {
    const days = line.received_dates ?? [];
    if (days.length === 0) return null;
    const first = days[0].date;
    const last = days[days.length - 1].date;
    return first === last ? formatDay(first) : `${formatDay(first)} – ${formatDay(last)}`;
};

// The hover: every day with its units, oldest first.
export const putHereBreakdown = (line: WriteOffLine): string =>
    (line.received_dates ?? []).map((d) => `${formatDay(d.date)} × ${d.qty}`).join(" · ");

export interface SelectionEntry {
    checked: boolean;
    /** What the admin typed — kept as a string so a cleared box stays empty. */
    qty: string;
}
export type Selection = Record<string, SelectionEntry>;

// Coerce whatever the BE sent into numbers once, at the edge.
export const normalizeLines = (raw: unknown): WriteOffLine[] =>
    (Array.isArray(raw) ? raw : []).map((l: any) => {
        const unitsHere = Number(l?.units_here ?? 0);
        const allocated = Number(l?.allocated_qty ?? 0);
        const unitCost = Number(l?.unit_cost ?? 0);
        return {
            sku: String(l?.sku ?? ""),
            title: String(l?.title ?? l?.sku ?? ""),
            barcode: l?.barcode ?? null,
            thumbnail_url: l?.thumbnail_url ?? null,
            image_url: l?.image_url ?? null,
            units_here: unitsHere,
            allocated_qty: allocated,
            writable_qty: Number.isFinite(Number(l?.writable_qty)) ? Number(l.writable_qty) : Math.max(0, unitsHere - allocated),
            free_stock: Number(l?.free_stock ?? 0),
            unit_cost: Number.isFinite(unitCost) ? unitCost : 0,
            line_cost: Number(l?.line_cost ?? unitsHere * unitCost),
            received_first: l?.received_first ?? null,
            received_last: l?.received_last ?? null,
            received_dates: normalizeReceived(l?.received_dates),
        };
    });

export const normalizeProduct = (raw: any): WriteOffProduct => ({
    sku: String(raw?.sku ?? ""),
    title: String(raw?.title ?? raw?.sku ?? ""),
    barcode: raw?.barcode ?? null,
    thumbnail_url: raw?.thumbnail_url ?? null,
    image_url: raw?.image_url ?? null,
    free_stock: Number(raw?.free_stock ?? 0),
    total_units: Number(raw?.total_units ?? 0),
});

// By-SKU rows → WriteOffLine, one per location, carrying the product so the
// same table, selection and confirm code renders both shapes.
export const normalizeSkuLines = (raw: unknown, product: WriteOffProduct): WriteOffLine[] =>
    (Array.isArray(raw) ? raw : []).map((l: any) => {
        const unitsHere = Number(l?.units_here ?? 0);
        const allocated = Number(l?.allocated_qty ?? 0);
        const unitCost = Number(l?.unit_cost ?? 0);
        return {
            sku: product.sku,
            title: product.title,
            barcode: product.barcode,
            thumbnail_url: product.thumbnail_url,
            image_url: product.image_url,
            location: String(l?.location ?? ""),
            location_name: String(l?.location_name ?? l?.location ?? ""),
            sellable: l?.sellable === "sellable" ? "sellable" : "unsellable",
            units_here: unitsHere,
            allocated_qty: allocated,
            writable_qty: Number.isFinite(Number(l?.writable_qty)) ? Number(l.writable_qty) : Math.max(0, unitsHere - allocated),
            free_stock: product.free_stock,
            unit_cost: Number.isFinite(unitCost) ? unitCost : 0,
            line_cost: Number(l?.line_cost ?? unitsHere * unitCost),
            received_first: l?.received_first ?? null,
            received_last: l?.received_last ?? null,
            received_dates: normalizeReceived(l?.received_dates),
        };
    });

// Fresh selection for a result set: nothing ticked, every quantity
// defaulted to the full unallocated amount.
export const initialSelection = (lines: WriteOffLine[]): Selection => {
    const out: Selection = {};
    for (const l of lines) out[lineKey(l)] = { checked: false, qty: String(l.writable_qty) };
    return out;
};

// Parse a quantity box: a positive integer, or null when blank/invalid.
export const parseQty = (raw: string): number | null => {
    const s = String(raw ?? "").trim();
    if (!/^\d+$/.test(s)) return null;
    const n = Number(s);
    return n >= 1 ? n : null;
};

export type LineProblem =
    | "invalid_qty"        // blank / zero / not a whole number
    | "over_writable"      // more than units here − allocated: always blocks
    | "over_free_stock";   // more than SKU-wide free stock: blocks for sold offline, warns otherwise

// Problems on one ticked line at a quantity. Ordered worst-first so the
// caller can show the single most important one. In by-SKU mode the free
// stock is shared across the rows and judged on the total instead
// (validateSelection), so the per-row check is switched off there.
export const lineProblems = (line: WriteOffLine, qty: number | null, { checkFreeStock = true } = {}): LineProblem[] => {
    if (qty === null) return ["invalid_qty"];
    const out: LineProblem[] = [];
    if (qty > line.writable_qty) out.push("over_writable");
    if (checkFreeStock && qty > line.free_stock) out.push("over_free_stock");
    return out;
};

// Does this problem stop the write-off for this reason? Allocated units
// can never go; reserved (SKU-wide) only blocks when the units are being
// sold to someone else.
export const isBlocking = (problem: LineProblem, reason: string | null): boolean => {
    if (problem === "invalid_qty" || problem === "over_writable") return true;
    return reason === "sold_offline";
};

export interface ValidatedLine {
    /** lineKey of the source row: location barcode by SKU, else the SKU. */
    key: string;
    sku: string;
    title: string;
    /** By SKU only. */
    location?: string;
    location_name?: string;
    quantity: number;
    unit_cost: number;
    cost_value: number;
    warnings: LineProblem[];
}

export type SelectionError = "no_lines" | "no_reason" | "invalid_qty" | "over_writable" | "over_free_stock";

export interface SelectionResult {
    ok: boolean;
    error?: SelectionError;
    errorSku?: string;
    lines: ValidatedLine[];
    total_units: number;
    total_cost: number;
    /** Any non-blocking warnings across the ticked lines (shown in the confirm). */
    warnings: boolean;
    /** By SKU: the ticked total exceeds the SKU's free stock (blocks for sold offline, warns otherwise). */
    over_free_total: boolean;
}

export interface ValidateOptions {
    /**
     * By-SKU mode: every row is the same SKU, so free stock is one number
     * shared by all of them and the TOTAL ticked is what must fit — never
     * each row on its own (that would let two locations oversell together).
     */
    sharedFreeStock?: number;
}

// The ticked lines, validated for the chosen reason. `ok` is false when
// nothing is ticked, no reason is chosen, or any line has a blocking
// problem; non-blocking problems come back as warnings on the line.
export const validateSelection = (
    lines: WriteOffLine[],
    selection: Selection,
    reason: string | null,
    { sharedFreeStock }: ValidateOptions = {},
): SelectionResult => {
    const shared = typeof sharedFreeStock === "number";
    const out: ValidatedLine[] = [];
    let error: SelectionError | undefined;
    let errorSku: string | undefined;
    let warnings = false;
    for (const line of lines) {
        const entry = selection[lineKey(line)];
        if (!entry?.checked) continue;
        const qty = parseQty(entry.qty);
        const problems = lineProblems(line, qty, { checkFreeStock: !shared });
        const blocking = problems.find((p) => isBlocking(p, reason));
        if (blocking && !error) { error = blocking; errorSku = line.sku; }
        const soft = problems.filter((p) => !isBlocking(p, reason));
        if (soft.length > 0) warnings = true;
        const quantity = qty ?? 0;
        out.push({
            key: lineKey(line),
            sku: line.sku,
            title: line.title,
            location: line.location,
            location_name: line.location_name,
            quantity,
            unit_cost: line.unit_cost,
            cost_value: Math.round(quantity * line.unit_cost * 100) / 100,
            warnings: soft,
        });
    }
    const total_units = out.reduce((a, l) => a + l.quantity, 0);
    let over_free_total = false;
    if (shared && out.length > 0 && total_units > sharedFreeStock) {
        over_free_total = true;
        if (isBlocking("over_free_stock", reason)) {
            if (!error) { error = "over_free_stock"; errorSku = out[0].sku; }
        } else {
            warnings = true;
        }
    }
    if (out.length === 0) error = "no_lines";
    else if (!isWriteOffReason(reason) && !error) error = "no_reason";
    return {
        ok: !error,
        error,
        errorSku,
        lines: out,
        total_units,
        total_cost: Math.round(out.reduce((a, l) => a + l.cost_value, 0) * 100) / 100,
        warnings,
        over_free_total,
    };
};

export const formatMoney = (n: number, currency = "£") =>
    `${currency}${(Number.isFinite(n) ? n : 0).toFixed(2)}`;

// ---- History ---------------------------------------------------------

export interface HistoryFilters {
    from: string;
    to: string;
    location: string;
    sku: string;
    reason: string;
}

export const EMPTY_HISTORY_FILTERS: HistoryFilters = { from: "", to: "", location: "", sku: "", reason: "" };

// Query string for GET /write-off/history (and its /export twin). Empty
// filters are omitted so the BE applies nothing for them.
export const historyQuery = (
    warehouse: string,
    filters: HistoryFilters,
    paging?: { page: number; pageSize: number },
): string => {
    const p = new URLSearchParams();
    p.set("warehouse", warehouse);
    if (filters.from) p.set("from", filters.from);
    if (filters.to) p.set("to", filters.to);
    if (filters.location.trim()) p.set("location", filters.location.trim());
    if (filters.sku.trim()) p.set("sku", filters.sku.trim());
    if (filters.reason) p.set("reason", filters.reason);
    if (paging) {
        p.set("page", String(paging.page));
        p.set("page_size", String(paging.pageSize));
    }
    return p.toString();
};

export interface HistoryRow {
    id: number;
    created_at: string;
    written_off_by_name: string | null;
    warehouse: string;
    location: string;
    location_name: string;
    sku: string;
    title: string;
    quantity: number;
    unit_cost: number;
    reason: string;
    reference: string | null;
    cost_value: number;
}

export const formatWhen = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${d.toLocaleDateString("en-GB")} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};
